import React from 'react';

import { FACTOR_LABELS, SUBCHECK_LABELS } from './const/risks.labels';
import { slackLabel, slackLink } from './guardrails';
import ComplexityTag, { StatusFactor, StatusTagConfig } from './tag';
import { VariableType } from '@/constants';
import { VariableInfo } from '@/lib/App';

const DOC_BASE =
  'https://docs.google.com/document/d/13b1QoOh5b-wrloFUi2h6vCfPen97OSQupbL2_sFZwAo/edit?tab=t.wkhmi135l02q';

const CONFIG: StatusTagConfig = {
  tagLabel: 'Complexity',
  passLabel: 'Healthy',
  failLabel: 'Risky',
  docLink: DOC_BASE,
  slackLink: slackLink,
  slackLabel: slackLabel,
  footerNote:
    'Complexity indicators are evaluated against design-time thresholds to detect variables that may degrade system performance at scale.',
  reduceLink: DOC_BASE,
};

const buildFactors = (variable: VariableInfo): StatusFactor[] => {
  const varType = variable?.type?.toLowerCase();
  const isJson = varType === VariableType.JSON;
  const isMapOrString =
    varType === VariableType.MAP || varType === VariableType.STRING;
  const complexityFactors = variable?.complexity?.complexityFactors ?? {};

  const valueComplexitySubChecks = [
    ...(complexityFactors?.['valueSizeKB']
      ? [
          {
            label: SUBCHECK_LABELS.size,
            description:
              'The total serialised byte size of the variable value. Large values increase SDK payload size and memory usage at runtime.',
            value: `${complexityFactors?.['valueSizeKB']?.value ?? 0} KB`,
            threshold: `≤ ${complexityFactors?.['valueSizeKB']?.threshold} KB`,
            passed: !complexityFactors?.['valueSizeKB']?.violated,
          },
        ]
      : []),
    ...(isJson
      ? [
          ...(complexityFactors?.['valueKeys']
            ? [
                {
                  label: SUBCHECK_LABELS.keyValuePairs,
                  description:
                    'The total number of key-value pairs across all levels of the JSON value. A high count increases parsing overhead and memory consumption.',
                  value: `${complexityFactors?.['valueKeys']?.value ?? 0}`,
                  threshold: `≤ ${complexityFactors?.['valueKeys']?.threshold}`,
                  passed: !complexityFactors?.['valueKeys']?.violated,
                },
              ]
            : []),
          ...(complexityFactors?.['valueDepth']
            ? [
                {
                  label: SUBCHECK_LABELS.jsonDepth,
                  description:
                    'The maximum nesting depth of the JSON value. Deeply nested structures are harder to parse and increase the risk of stack overflows in consumers.',
                  value: `${complexityFactors?.['valueDepth']?.value ?? 0}`,
                  threshold: `≤ ${complexityFactors?.['valueDepth']?.threshold}`,
                  passed: !complexityFactors?.['valueDepth']?.violated,
                },
              ]
            : []),
        ]
      : []),
  ];

  const constraintComplexitySubChecks = [
    ...(complexityFactors?.['constraintCardinality']
      ? [
          {
            label: SUBCHECK_LABELS.numberOfConstraints,
            description:
              'The total number of constraint rules applied to this variable. A high constraint count increases evaluation time on every SDK request.',
            value: `${complexityFactors?.['constraintCardinality']?.value ?? 0}`,
            threshold: `≤ ${complexityFactors?.['constraintCardinality']?.threshold}`,
            passed: !complexityFactors?.['constraintCardinality']?.violated,
          },
        ]
      : []),
    ...(complexityFactors?.['constraintGroups']
      ? [
          {
            label: SUBCHECK_LABELS.groupsPerSegP,
            description:
              'The number of distinct constraint groups within a rollout. Each group is evaluated independently, so more groups means higher per-request compute cost.',
            value: `${complexityFactors?.['constraintGroups']?.value ?? 0}`,
            threshold: `≤ ${complexityFactors?.['constraintGroups']?.threshold}`,
            passed: !complexityFactors?.['constraintGroups']?.violated,
          },
        ]
      : []),
  ];

  return [
    ...(complexityFactors?.['targetCardinality']
      ? [
          {
            label: FACTOR_LABELS.targetCardinality,
            passed: !complexityFactors?.['targetCardinality']?.violated,
            learnMoreLink: `${DOC_BASE}#heading=target-cardinality`,
            subChecks: [
              {
                label: SUBCHECK_LABELS.rolloutTargets,
                description:
                  'The total number of distinct rollout targets defined for this variable. A high target count increases SDK lookup time and memory footprint.',
                value: `${complexityFactors?.['targetCardinality']?.value ?? 0}`,
                threshold: `≤ ${complexityFactors?.['targetCardinality']?.threshold}`,
                passed: !complexityFactors?.['targetCardinality']?.violated,
              },
            ],
          },
        ]
      : []),
    ...((isJson || isMapOrString) &&
    (complexityFactors?.['valueSizeKB'] ||
      complexityFactors?.['valueKeys'] ||
      complexityFactors?.['valueDepth'])
      ? [
          {
            label: FACTOR_LABELS.valueComplexity,
            passed: isMapOrString
              ? !complexityFactors?.['valueSizeKB']?.violated
              : valueComplexitySubChecks.every((subCheck) => subCheck.passed),
            learnMoreLink: `${DOC_BASE}#heading=value-complexity`,
            subChecks: valueComplexitySubChecks,
          },
        ]
      : []),
    ...(complexityFactors?.['constraintCardinality'] ||
    complexityFactors?.['constraintGroups']
      ? [
          {
            label: FACTOR_LABELS.constraintLogic,
            passed: constraintComplexitySubChecks.every(
              (subCheck) => subCheck.passed,
            ),
            learnMoreLink: `${DOC_BASE}#heading=constraint-logic`,
            subChecks: constraintComplexitySubChecks,
          },
        ]
      : []),
    ...(complexityFactors?.['changeFrequency']
      ? [
          {
            label: FACTOR_LABELS.changeFrequency,
            passed: !complexityFactors?.['changeFrequency']?.violated,
            learnMoreLink: `${DOC_BASE}#heading=change-frequency`,
            subChecks: [
              {
                label: SUBCHECK_LABELS.numberOfChanges,
                description:
                  'How many times this variable has been modified in the past 60 days. Frequent changes increase the risk of cache invalidation storms and unexpected value propagation across services.',
                value: `${complexityFactors?.['changeFrequency']?.value ?? 0}`,
                threshold: `≤ ${complexityFactors?.['changeFrequency']?.threshold}`,
                passed: !complexityFactors?.['changeFrequency']?.violated,
              },
            ],
          },
        ]
      : []),
  ];
};

interface Props {
  variable: VariableInfo;
}

const RisksTag = ({ variable }: Props) => {
  const factors = buildFactors(variable);

  if (!factors.length) return null;

  const failedCount = factors.filter((f) => !f.passed).length;
  const failLabel =
    failedCount > 2
      ? 'Severe'
      : failedCount >= 1
        ? 'Caution'
        : CONFIG.failLabel;
  const failColor: 'error' | 'warning' = failedCount > 2 ? 'error' : 'warning';
  const config = { ...CONFIG, failLabel, failColor };

  return (
    <ComplexityTag config={config} factors={factors} dataTestId="risks-tag" />
  );
};

export default RisksTag;
